/**
 * A text pill (TMMAPP-0103, visual contract §9): radius 11, padding 3 9, 11/500, not uppercase. The
 * label is always text, so the colour never carries it alone. `tone` paints a status tone through
 * `toneColours` (info, warning, error, success, neutral); `wash` paints the prototype's two soft
 * pill fills (TMMAPP-0108, the 0103 review): `brand` is the light blue `brandSoft` with `text` (the
 * prototype's `rule.fire` / `allocate` / virtual account pills), `grey` is `surface2` with `text2`
 * (`commitment.revise`, archive, share). Both are opaque fills that differ from `surface` and from the
 * `selected` layer, so a pill keeps its fill on a white row and on a selected (live) row. Both are
 * guaranteed pairs. A pill is a label, not a control.
 */
import type { ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import { resolveTokens } from '@/theme/theme';
import { toneColours, type Tone } from '@/theme/statusTones';

export type PillPaint = { tone: Tone } | { wash: 'brand' | 'grey' } | { brand: true };

type PillProps = PillPaint & {
  children: ReactNode;
  /** A test hook and a styling hook: the paint's name. */
  'data-paint'?: string;
  /** The live pill's 10px. */
  small?: boolean;
};

export function Pill({ children, 'data-paint': paint, small = false, ...rest }: PillProps) {
  const tokens = resolveTokens(useTheme());
  let background: string;
  let ink: string;
  let border: string | null = null;
  if ('wash' in rest) {
    background = rest.wash === 'brand' ? tokens.brandSoft : tokens.surface2;
    ink = rest.wash === 'brand' ? tokens.text : tokens.text2;
  } else if ('brand' in rest) {
    background = tokens.brand;
    ink = tokens.brandInk;
  } else {
    ({ background, ink, border } = toneColours(tokens, rest.tone));
  }
  return (
    <Box
      component="span"
      data-paint={paint}
      sx={{
        display: 'inline-block',
        maxWidth: '100%',
        borderRadius: '11px',
        padding: '3px 9px',
        fontSize: small ? 10 : 11,
        lineHeight: small ? '14px' : '16px',
        ...(small ? { textTransform: 'uppercase', letterSpacing: '0.06em' } : {}),
        fontWeight: 500,
        overflowWrap: 'anywhere',
        backgroundColor: background,
        color: ink,
        ...(border === null ? {} : { border: `1px solid ${border}` }),
      }}
    >
      {children}
    </Box>
  );
}
